import React, { useEffect, useState } from 'react';

import { Box, Button, TextField } from '@mui/material';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userReducer } from '../../store/reducers/user.reducer';
import { getCreditsSuccess, getIsSendingCredits, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';

const { sendCredits } = userReducer.actions;

export const CreditsPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isSendingCredits = useAppSelector(getIsSendingCredits);
  const creditsSuccess = useAppSelector(getCreditsSuccess);
  const [email, setEmail] = useState('');
  const [credits, setCredits] = useState(100);
  const [emailError, setEmailError] = useState('');
  const [creditsError, setCreditsError] = useState('');

  // Check if user is admin, redirect to forbidden if not
  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // Reset form when credits are sent successfully
  useEffect(() => {
    if (creditsSuccess) {
      setEmail('');
      setCredits(100);
    }
  }, [creditsSuccess]);

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError('Please provide an email');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError('');
    return true;
  };

  const validateCredits = (credits: number) => {
    if (!credits || credits <= 0) {
      setCreditsError('Credits must be greater than 0');
      return false;
    }

    setCreditsError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isEmailValid = validateEmail(email);
    const isCreditsValid = validateCredits(credits);

    if (!isEmailValid || !isCreditsValid) {
      return;
    }

    dispatch(
      sendCredits({
        email,
        credits,
      }),
    );
  };

  // Don't render anything if user is not admin (will redirect)
  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  return (
    <Box>
      <AdminPageHeader title="Add User Credits" subtitle="Add credits to an existing user account." />
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="User Email"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          disabled={isSendingCredits}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          id="credits"
          label="Credits"
          name="credits"
          type="number"
          value={credits}
          onChange={(e) => setCredits(Number(e.target.value))}
          error={!!creditsError}
          helperText={creditsError || 'Default: 100 credits'}
          disabled={isSendingCredits}
          inputProps={{ min: 1 }}
        />

        <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={isSendingCredits}>
          {isSendingCredits ? 'Adding Credits...' : 'Add Credits'}
        </Button>
      </Box>
    </Box>
  );
};
